'use client'

import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'

// Remembers a feed's scroll position by ANCHORING on an item id (robust to
// masonry reflow / column changes, unlike a pixel offset — Pinterest/Instagram
// style). A module singleton, so it survives client-side navigation and resets
// on a hard reload (which is fine: the in-memory feed cache resets too).
const store = new Map() // feedKey -> anchorId

export const getAnchor = (key) => store.get(key) || null
export const setAnchor = (key, id) => {
  if (key && id) store.set(key, id)
}

// SSR-safe layout effect (avoids the server warning; runs sync before paint on
// the client so the close→anchor scroll lands with no visible intermediate jump).
export const useIsoLayoutEffect =
  typeof document !== 'undefined' ? useLayoutEffect : useEffect

// Find a mounted tile by data-feed-id. Scans in JS (rather than a CSS selector)
// so Firestore-style ids with awkward characters can't break the query.
function findTile(id) {
  if (!id || typeof document === 'undefined') return null
  const nodes = document.querySelectorAll('[data-feed-id]')
  for (const n of nodes) {
    if (n.getAttribute('data-feed-id') === id) return n
  }
  return null
}

// Id of the top-most feed tile currently visible below the fixed header. Probes
// several depths (and near both edges, for multi-column grids) so a header /
// filter bar / featured block above the grid can't hide the first real tile.
function topVisibleId() {
  if (typeof document === 'undefined') return null
  const header = document.querySelector('header')
  const base = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0
  const w = window.innerWidth
  // 0.25/0.5/0.75 cover 2- to 5-column grids; the loop returns on the first hit,
  // so this is usually 1 elementFromPoint call once scrolled into the grid.
  const xs = [Math.round(w * 0.25), Math.round(w * 0.5), Math.round(w * 0.75)]
  for (const dy of [20, 140, 320]) {
    const y = base + dy
    if (y >= window.innerHeight) break
    for (const x of xs) {
      const el = document.elementFromPoint(x, y)
      const id = el?.closest?.('[data-feed-id]')?.getAttribute('data-feed-id')
      if (id) return id
    }
  }
  return null
}

/**
 * @param feedKey stable key for this feed ('explore', `profile:${uid}:${tab}`)
 * @param opts.enabled defer saving/restoring until the feed is ready
 * @returns { focusTile(id), saveAnchor(id) }
 */
export default function useFeedScrollMemory(feedKey, { enabled = true } = {}) {
  const keyRef = useRef(feedKey)
  keyRef.current = feedKey

  const focusTile = useCallback((id, block = 'center') => {
    const el = findTile(id)
    if (!el) return false
    el.scrollIntoView({ block, behavior: 'auto' })
    return true
  }, [])

  const saveAnchor = useCallback((id) => setAnchor(keyRef.current, id), [])

  // Save the top-most visible tile, throttled to ~150ms (not every frame) so the
  // hit-testing (elementFromPoint) doesn't add jank to scrolling on low-end
  // phones. 150ms granularity is plenty for "roughly where I was".
  useEffect(() => {
    if (!enabled) return
    let last = 0
    let raf = 0
    const onScroll = () => {
      const now = Date.now()
      if (now - last < 150 || raf) return
      last = now
      raf = requestAnimationFrame(() => {
        raf = 0
        const id = topVisibleId()
        if (id) setAnchor(keyRef.current, id)
      })
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [enabled])

  // Restore on mount: once the anchor tile exists and layout has settled, center
  // it (masonry lays out async, and Profile refetches — so poll briefly). Abort
  // if the user takes over scrolling first, so we never fight them.
  useEffect(() => {
    if (!enabled) return
    const id = getAnchor(keyRef.current)
    if (!id) return

    let done = false
    const finish = () => {
      done = true
      clearInterval(interval)
      clearTimeout(timeout)
      window.removeEventListener('wheel', cancel)
      window.removeEventListener('touchmove', cancel)
      window.removeEventListener('keydown', cancel)
    }
    const cancel = () => finish()
    const tryFocus = () => {
      if (done) return
      if (focusTile(id)) finish()
    }

    const raf = requestAnimationFrame(tryFocus)
    const interval = setInterval(tryFocus, 120)
    const timeout = setTimeout(finish, 4000)
    // User intent cancels a pending restore (but not the programmatic scroll,
    // which fires 'scroll', not these).
    window.addEventListener('wheel', cancel, { passive: true })
    window.addEventListener('touchmove', cancel, { passive: true })
    window.addEventListener('keydown', cancel)

    return () => {
      cancelAnimationFrame(raf)
      finish()
    }
  }, [enabled, focusTile])

  return { focusTile, saveAnchor }
}
